import { TextInput } from "@/block-system/brickz/components/ui/TextInput";
import { FieldBlock } from "block-system/blocks/Form/Field/Block";
import type { FieldBlock as FieldBlockType } from "block-system/blocks/Form/Field/types";
import { Button } from "block-system/brickz/components/ui/Button";
import { useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { tableRecordFormToTableValues } from "server/models/util/tables";
import { APIRecord } from "server/services/tables/types/records";
import type { APIFieldLatest } from "server/services/tables/types/tables-latest";
import { tableFieldToFormField } from "server/services/tables/utils/tables-to-forms";
import { cn } from "utils/cn";

type FormData = Record<string, any>;

interface TableRecordFormBaseProps {
  fields: APIFieldLatest[];
  record: APIRecord | Partial<APIRecord> | undefined;
  onCancel: () => void;
  tableId: string | undefined;
  formLabel?: string;
}

interface TableRecordCreateFormProps extends TableRecordFormBaseProps {
  onSubmit: (data: FormData) => void;
  isSubmitting: boolean;
}

interface TableRecordUpdateFormProps extends TableRecordFormBaseProps {
  onSubmit: (data: FormData) => void;
  isSubmitting: boolean;
}

interface TableRecordDeleteFormProps extends TableRecordFormBaseProps {
  isDeleting: boolean;
  onDelete: () => void;
}

type TableRecordViewFormProps = TableRecordFormBaseProps;

type TableRecordFormProps =
  | TableRecordCreateFormProps
  | TableRecordUpdateFormProps
  | TableRecordDeleteFormProps
  | TableRecordViewFormProps;

export function TableRecordForm(props: TableRecordFormProps) {
  const fieldBlocks = useMemo(
    () => getFieldBlocks({ tableFields: props.fields, tableId: props.tableId }),
    [props.fields, props.tableId]
  );

  const defaultValues = useMemo(() => {
    return getFieldBlocksDefaultValues({
      fieldBlocks,
      record: props.record,
    });
  }, [props.record, fieldBlocks]);

  const methods = useForm<FormData>({
    mode: "onBlur",
    defaultValues: defaultValues,
  });

  const isSubmitting = "isSubmitting" in props ? props.isSubmitting : false;
  const canDeleteRecord = "onDelete" in props;
  const canUpsertRecord = "onSubmit" in props;

  const isReadonly = canDeleteRecord === false && canUpsertRecord === false;

  const handleOnSubmit: SubmitHandler<FormData> = (data) => {
    if (isReadonly) {
      return;
    }
    const hasSubmitHandler = "onSubmit" in props;
    if (!hasSubmitHandler) {
      return;
    }

    const tableData = tableRecordFormToTableValues(data, fieldBlocks);
    props.onSubmit(tableData);
  };

  return (
    <form onSubmit={methods.handleSubmit(handleOnSubmit)}>
      <FormProvider {...methods}>
        <fieldset className="flex flex-col gap-y-[30px]" disabled={isReadonly}>
          <legend className={"sr-only"}>{props.formLabel ?? "New item"}</legend>
          <FieldBlocks fieldBlocks={fieldBlocks} />
        </fieldset>
      </FormProvider>

      {canUpsertRecord || canDeleteRecord ? <div className="h-10" /> : null}

      {canUpsertRecord ? (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={props.onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </div>
      ) : null}

      {canDeleteRecord ? (
        <span className="mt-8 text-sm">
          You can also{" "}
          <button
            className={cn(
              "cursor-pointer text-destructive underline transition-all"
            )}
            type="button"
            onClick={() => {
              props.onDelete();
            }}
          >
            delete this record
          </button>
        </span>
      ) : null}
    </form>
  );
}

function FieldBlocks({ fieldBlocks }: { fieldBlocks: FieldBlockType[] }) {
  if (fieldBlocks.length === 0) {
    return <span className="text-base">No fields to display</span>;
  }

  return fieldBlocks.map((fieldBlock, i) => {
    return (
      <FieldBlock
        key={i}
        block={fieldBlock}
        blocks={[]}
        blockId={i.toString()}
      />
    );
  });
}

export function getFieldBlocks({
  tableFields,
  tableId,
}: {
  tableFields: APIFieldLatest[];
  tableId: string | undefined;
}): FieldBlockType[] {
  const fieldBlocks: FieldBlockType[] = [];

  tableFields.forEach((tableField) => {
    try {
      const fieldBlock = tableFieldToFormField(tableField, tableId);

      if (fieldBlock) {
        fieldBlocks.push({
          ...fieldBlock,
          config: {
            ...fieldBlock.config,
            name: `f${tableField.id}`,
          },
        });
      }
    } catch (err) {
      console.error(err);
    }
  });

  return fieldBlocks;
}

export function getFieldBlocksDefaultValues(props: {
  record: APIRecord | Partial<APIRecord> | undefined;
  fieldBlocks: FieldBlockType[];
}) {
  const data = props.record?.data;
  if (!data) {
    return undefined;
  }

  const values = Object.keys(data).reduce((defaultValues, fieldKey) => {
    const fieldBlock = props.fieldBlocks.find(
      (f) => f.config.tablesFieldKey === fieldKey
    );

    if (!fieldBlock) {
      return { ...defaultValues, [fieldKey]: undefined };
    }

    if (!data[fieldKey]) {
      return { ...defaultValues, [fieldKey]: undefined };
    }

    const config = fieldBlock.config;

    switch (config.inputType) {
      case "date-picker": {
        const date = new Date(data[fieldKey] as string);
        // convert to UTC if time is not included
        if (!config.includeTime) {
          date.setTime(date.getTime() + date.getTimezoneOffset() * 60000);
        }

        return { ...defaultValues, [fieldKey]: date };
      }
      case "dropdown": {
        if (config.tableId && config.tableFieldId) {
          return { ...defaultValues, [fieldKey]: data[fieldKey] };
        }

        const recordValue = data[fieldKey];
        if (config.multiSelect) {
          if (!Array.isArray(recordValue)) {
            return { ...defaultValues, [fieldKey]: null };
          }

          const transformedValue = recordValue.map((item) => {
            return item?.value;
          });

          return { ...defaultValues, [fieldKey]: transformedValue };
        }

        if (typeof recordValue === "object" && "value" in recordValue) {
          return { ...defaultValues, [fieldKey]: recordValue.value };
        }

        return { ...defaultValues, [fieldKey]: recordValue };
      }
      case "url":
        return {
          ...defaultValues,
          [fieldKey]: (data[fieldKey] as { text: string; link: string }).link,
        };
      case "multi-url":
        return {
          ...defaultValues,
          [fieldKey]: (data[fieldKey] as { text: string; link: string }[])
            .map((item) => item.link)
            .join(","),
        };
      case "formula":
        const { value, status } = data[fieldKey] as {
          value: string;
          status: string;
        };
        if (status === "error") {
          return {
            ...defaultValues,
            [fieldKey]: "",
          };
        }
        return {
          ...defaultValues,
          [fieldKey]: value,
        };
      default:
        return { ...defaultValues, [fieldKey]: data[fieldKey] };
    }
  }, {});

  return values;
}
